import {inject, Injectable} from '@angular/core';
import {CommonsService} from '../core/commons.service';
import {AConst} from '../core/a-const.enum';
import {StoredSearchServiceImplementation, StoredSearchService} from './stored-search.service';
import {
  RootSearchView,
  SearchViewPath
} from '../core/definitions/search-objects';
import {QueryFieldMenuService} from './query-field-menu.service';
import {ResultViewService} from './result-view.service';
import {MetaField} from '../core/definitions/meta-field';
import {SearchStateService} from './search-state.service';
import {SearchFilterService} from './search-filter.service';
import {SearchExecutorService} from './search-executor.service';
import {SearchResultSelectionsService} from './search-result-selections.service';
import {SearchFilters} from '../core/definitions/search-filters';
import {UserCacheService} from '../core/user-cache.service';
import {SearchTemplateGroupService} from './search-template-group.service';
import {SearchContainer, SearchContainerParams} from '../core/definitions/search-container';
import {StoredSearch} from '../core/definitions/stored-search';
import {PrimusRouterService} from '../core/primus-router.service';
import {SearchViewSortOrderMenuService} from "./search-view-sort-order-menu.service";
import {SearchViewFilterService} from "./search-view-filter.service";
import {SearchResultSelectionsSorter} from "../core/search-result-selections-sorter";
import {getChildOrderFieldName, getOrderFieldName} from './child-field-order';

export interface SearchMenuPath {
  title: string;
  targetId: string;
  path: string;
}

@Injectable({
  providedIn: 'root'
})
export class SearchHandlerService {
  // Injections
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly queryFieldMenuService: QueryFieldMenuService = inject(QueryFieldMenuService);
  private readonly resultViewService: ResultViewService = inject(ResultViewService);
  private readonly searchExecutorService: SearchExecutorService = inject(SearchExecutorService);
  private readonly searchFilterService: SearchFilterService = inject(SearchFilterService);
  private readonly storedSearchService: StoredSearchService = inject(StoredSearchService);
  private readonly searchResultSelectionsService: SearchResultSelectionsService = inject(SearchResultSelectionsService);
  private readonly searchStateService: SearchStateService = inject(SearchStateService);
  private readonly searchTemplateGroupService: SearchTemplateGroupService = inject(SearchTemplateGroupService);
  private readonly sortOrderMenuService: SearchViewSortOrderMenuService = inject(SearchViewSortOrderMenuService);
  private readonly searchViewFilterService: SearchViewFilterService = inject(SearchViewFilterService);
  private readonly userCacheService: UserCacheService = inject(UserCacheService);
  private readonly searchResultSelectionsSorter: SearchResultSelectionsSorter = inject(SearchResultSelectionsSorter);

  private fsi: StoredSearchServiceImplementation;

  /**
   * @param params Params for creating search container
   * @param.searchViewName Name of search view to retrieve from
   * server.
   * @param.filter Search filters, which will be added to search view
   * filters
   * @param.targetState The target state name that will be used when
   * setting state parameters.
   * @param.stateParams Initial state parameters that will be used
   * to set some search container attributes. Relevant for
   * page reload.
   * @param.targetObject An object that will be associated with the
   * search container. The object can later be accessed directly
   * from the search container object.
   */
  async createSearchContainer(params: SearchContainerParams): Promise<SearchContainer> {
    const filters = params.filters || {};
    const view = await this.getSearchView(params, filters);
    const searchContainer: SearchContainer = new SearchContainer();
    if (view) {
      searchContainer.restrictions = params.restrictions;
      searchContainer.rows = params.rows;
      searchContainer.searchPostFn = params.searchPostFn;
      searchContainer.searchResultViews = view.search_result_views;
      searchContainer.searchView = view;
      searchContainer.state.targetState = params.targetState;
      searchContainer.targetObject = params.targetObject;
      searchContainer.qOp = params.qOp;
      this.searchTemplateGroupService.setTemplateGroup(searchContainer, params.templateGroupId);
      await this.searchTemplateGroupService.setListFieldTemplateGroup(searchContainer, params.listFieldTemplateId);

      searchContainer.used = params.used;
      searchContainer.keepSelected = params.keepSelected;
      this.searchResultSelectionsService.setSelections(searchContainer, params.selectedItems || []);
      this.resultViewService.calculateAndSetsDefaultRowsColumns(searchContainer);
      if (params.getStoredSearch) {
        this.fsi = this.storedSearchService.createFSI(searchContainer);
        await this.fsi.loadStoredSearches();
        const curStoredSearch = this.setSearchContainerFromStoredSearch(this.fsi, params);
        if (!curStoredSearch) {
          this.resultViewService.setDefaultSearchResultView(searchContainer);
        }
        this.setDefaultSearchContainerProps(searchContainer, params);
        return this.finishSearchContainerCreation(searchContainer, params);
      } else {
        this.resultViewService.setDefaultSearchResultView(searchContainer);
        this.setDefaultSearchContainerProps(searchContainer, params);
        return this.finishSearchContainerCreation(searchContainer, params);
      }
    } else {
      console.warn('No view: ' + params.searchViewName);
    }
  }

  getPathView(path: string, searchContainer: SearchContainer): SearchViewPath {
    let pathView: SearchViewPath;
    const searchView = searchContainer.searchView;
    if (searchView) {
      pathView = this.findPathView(path, searchView);
      if (!pathView) {
        console.warn('Could not find path \'' + path + '\' ' +
          'in menu view');
      }
    } else {
      console.warn('Search view not defined in search container');
    }
    return pathView;
  }

  setOrder(searchContainer: SearchContainer, fieldName: string, fieldInfo?: MetaField) {
    const curOrder = searchContainer.getOrder();
    let orderFieldName = getOrderFieldName(fieldName, fieldInfo);
    let parentQuery = '';
    // Handle ordering on child fields, which are field of objects that are indexed as child documents in Solr and
    // cannot be sorted the usual way. It's a bit shaky due to depending on child object types containing 'Item'.
    if (fieldInfo) {
      const childObjectType = fieldInfo.model_type || fieldInfo.parent_object_type;
      if (childObjectType?.includes('Item')) {
        [orderFieldName, parentQuery] = getChildOrderFieldName(searchContainer, childObjectType, orderFieldName);
      }
    }
    if (fieldName.indexOf(' asc') !== -1 || fieldName.indexOf(' desc') !== -1) {
      searchContainer.setOrder(fieldName, false, parentQuery);
    } else if (curOrder.indexOf(orderFieldName) === -1 || curOrder.indexOf(' desc') > 1) {
      searchContainer.setOrder(orderFieldName, false, parentQuery);
    } else {
      searchContainer.setOrder(  `${searchContainer.getOrder()} desc`, false, parentQuery);
    }
    if (searchContainer.getOrder().includes('{random}')) {
      searchContainer.orderRandomSeed = Math.round(Math.random() * 10000).toString();
    }
    this.searchExecutorService.resetSearchPosition(searchContainer);
    this.searchExecutorService.runSearch(searchContainer).then(() => {
      if (searchContainer.selections.selectedItems.length > 1) {
        // The sorting of selected items doesn't update the view automatically, probably must implement signaling
        // towards the
        this.searchResultSelectionsSorter.sortItems(searchContainer);
      }
    });
  }

  async goPathView(path: string, searchContainer: SearchContainer, keepFilterGroups: boolean = false) {
    const paramsSetFromStoredSearch = this.checkSetParamsFromStoredSearch(path, searchContainer);
    searchContainer.path = path;
    this.searchExecutorService.resetSearchPosition(searchContainer);
    searchContainer.searchResult = null;
    const pathView = await this.setCurrentPathView(searchContainer);
    if (!pathView.search_view.override_target) {
      this.resultViewService.setCurrentResultViewFromPathView(searchContainer, pathView);
      this.searchFilterService.resetFilter(searchContainer);
      const sortOrderMenus = await this.sortOrderMenuService.getSortOrderMenus(
        searchContainer.currentPathView.search_view.sort_order_menus);
      if (!paramsSetFromStoredSearch && searchContainer.orderIsDefault && sortOrderMenus) {
        searchContainer.setOrder(sortOrderMenus[0].order, true);
      }
      searchContainer.selections.allSelected = false;
      setTimeout(() => {
        this.runSearchAfterPathViewChange(searchContainer, keepFilterGroups).then(() => {
          this.searchFilterService.setDefaultCheckedFilters(searchContainer);
        });
      }, 1000);
    }
  }


  async goSearchMenu(menu: any, searchContainer: SearchContainer) {
    searchContainer.targetId = menu.targetId;
    searchContainer.objectCount = menu.objectCount;
    await this.goPathView(menu.path, searchContainer);
  }

  getSearchMenuPath(searchContainer: SearchContainer): SearchMenuPath[] {
    let pathView = searchContainer.currentPathView;
    const pathNames = this.getPath(searchContainer).split('/');
    const res = [];
    let path = '', pathSplit = '';
    pathNames.forEach((pathName, index) => {
      path += pathSplit + pathName;
      pathView = this.getPathView(path, searchContainer);
      if (pathView) {
        const targetId = pathName === 'folder' ? searchContainer.targetId : 'none';
        const menuPath = {
          title: pathView.search_view.title,
          targetId: targetId,
          path: null
        } as SearchMenuPath;
        if (index + 1 < pathNames.length) {
          menuPath.path = path;
        }
        res.push(menuPath);
        pathSplit = '/';
      }
    });
    return res;
  }

  setSearchItemIndex(searchContainer: SearchContainer, index: number) {
    searchContainer.searchItemIndex = index;
  }

  private async runSearchAfterPathViewChange(searchContainer: SearchContainer, keepFilterGroups: boolean = false): Promise<void> {
    if (!keepFilterGroups || !searchContainer.filtersFacets.filterGroups) {
      this.searchViewFilterService.setCheckFilterGroupsFromSearchView(searchContainer);
    }
    await this.searchExecutorService.runSearch(searchContainer);
    this.searchFilterService.checkMenusFromCheckedFilters(searchContainer);
    await this.searchFilterService.setCheckFilterGroups(searchContainer, false, true);
    if (searchContainer.storedSearch.curStoredSearchId) {
      this.searchFilterService.setTotalSelectedFilters(searchContainer);
      this.setCurrentStoredSearchName(searchContainer);
    }
  }

  private checkSetParamsFromStoredSearch(path: string, searchContainer: SearchContainer): boolean {
    let paramsSetFromStoredSearch = false
    const curStoredSearchId = searchContainer.storedSearch.curStoredSearchId;
    if (curStoredSearchId) {
      const pathParams = this.fsi.getSetStoredSearchPathParams(curStoredSearchId, path);
      if (pathParams) {
        this.fsi.setStoredSearchPathParamsOnSearchContainer(pathParams);
        paramsSetFromStoredSearch = true;
      }
    }
    return paramsSetFromStoredSearch;
  }

  private async finishSearchContainerCreation(searchContainer: SearchContainer,
                                              params: SearchContainerParams): Promise<SearchContainer> {
    this.setSearchResultViewDefaultRows(searchContainer);
    if (searchContainer.storedSearch.curStoredSearchId) {
      this.setCurrentStoredSearchName(searchContainer);
    }
    const pathView = await this.setCurrentPathView(searchContainer);
    this.searchFilterService.setCheckedFiltersFromSearchContainer(searchContainer,
      !!pathView, params.defaultCheckedFilters);

    if (params.runSearch) {
      await this.searchExecutorService.runSearch(searchContainer);
      this.searchFilterService.setDefaultCheckedFilters(searchContainer);
      return searchContainer;
    } else {
      return searchContainer;
    }
  }

  private findPathView(pathName: string, searchView: RootSearchView): SearchViewPath {
    let res: SearchViewPath;
    if (searchView.paths) {
      for (const path of searchView.paths) {
        if (path.path_name === pathName) {
          res = this.commons.copy(path);
          break;
        }
      }
    } else {
      console.warn('No paths!');
    }
    return res;
  }

  private getFolderDate(searchContainer: SearchContainer) {
    searchContainer.targetObject['date'] = searchContainer.targetObject.created_at;
    return searchContainer;
  }

  private getPath(searchContainer: SearchContainer): string {
    if (!searchContainer.path) {
      searchContainer.path = 'home';
    }
    return searchContainer.path;
  }

  private async getSearchView(params: SearchContainerParams, filters: any): Promise<RootSearchView> {
    let view = params.searchViewData;
    if (!view) {
      const viewName = params.searchViewName;
      if (!viewName) {
        console.error('Missing view name');
        return view;
      }
      view = await this.userCacheService.getSearchView(viewName);
    }
    if (view) {
      this.setSearchViewFilters(view, filters);
    }
    return view;
  }

  private setSearchViewFilters(view: RootSearchView, filters: any) {
    if (!(filters && Object.keys(filters).length)) {
      return;
    }
    for (const viewPath of view.paths) {
      viewPath.search_view.$$origFilters = viewPath.search_view.$$origFilters || viewPath.search_view.filters ||
        new SearchFilters();
      viewPath.search_view.filters = {...viewPath.search_view.$$origFilters};
      for (const [filterName, filterValue] of Object.entries(filters)) {
        this.setViewPathFilterValues(viewPath, filterName, filterValue);
      }
    }
  }

  private setViewPathFilterValues(viewPath: SearchViewPath, filterName: string, filterValue: any) {
    let filterValues = Array.isArray(filterValue) ? filterValue : [filterValue];
    let viewFilterValues = viewPath.search_view.filters[filterName];
    if (viewFilterValues) {
      viewFilterValues = Array.isArray(viewFilterValues) ? viewFilterValues : [viewFilterValues];
      viewFilterValues = viewFilterValues.concat(filterValues);
    } else {
      viewFilterValues = filterValues;
    }
    viewPath.search_view.filters[filterName] = viewFilterValues;
  }

  private setCurrentStoredSearchName(searchContainer: SearchContainer) {
    for (const storedSearch of searchContainer.storedSearch.storedSearches) {
      if (storedSearch.storedSearchId === searchContainer.storedSearch.curStoredSearchId) {
        searchContainer.storedSearch.curStoredSearchName = storedSearch.stored_search.name;
      }
    }
  }

  private async setCurrentPathView(searchContainer: SearchContainer): Promise<SearchViewPath> {
    const path = this.getPath(searchContainer);
    const pathView = this.getPathView(path, searchContainer);
    if (pathView) {
      searchContainer.currentPathView = pathView;
      if (searchContainer.extendedSearchParams) {
        searchContainer.extendedSearchParams.superobject_type_ids = pathView.search_view.superobject_types;
      }
      if (pathView.reset_query) {
        searchContainer.queryContainer.query = '';
      }
      await this.queryFieldMenuService.setSelectedQueryFieldMenu(searchContainer);
    }
    await this.searchViewFilterService.loadSetTargetObject(searchContainer);
    if (searchContainer.targetObject &&
      searchContainer.currentPathView.search_view.search_view_type === 'folder') {
      searchContainer.object = {
        artifact_id: searchContainer.targetObject[AConst.CREATED_BY_ID]
      };
      this.getFolderDate(searchContainer);
    }
    return pathView;
  }

  private setDefaultSearchContainerProps(searchContainer: SearchContainer, params: SearchContainerParams) {
    const toState = this.primusRouter.currentState();

    if (params.stateParams && toState &&
      toState.indexOf(params.targetState) === 0) {
      this.searchStateService.setSearchContainerPropsFromStateParams(searchContainer, params.stateParams);
      this.searchFilterService.setTotalSelectedFilters(searchContainer);
    }
  }

  private setSearchContainerFromStoredSearch(fsi: StoredSearchServiceImplementation, params: SearchContainerParams) {
    let curStoredSearch: StoredSearch;
    const curStoredSearchId = params.stateParams['cur_stored_search_id'];
    if (curStoredSearchId) {
      curStoredSearch = fsi.setCurrentStoredSearch(curStoredSearchId);
    } else if (params.useStoredSearch) {
      curStoredSearch = fsi.getCurrentOrDefaultStoredSearch();
      fsi.setCurrentStoredSearch(curStoredSearch);
    }
    if (curStoredSearch) {
      fsi.setStoredSearchPathParamsOnSearchContainer(curStoredSearch.stored_search.pathParams[curStoredSearch.stored_search.homePath]);
    }
    return curStoredSearch;
  }

  private setSearchResultViewDefaultRows(searchContainer: SearchContainer) {
    for (const searchResultView of searchContainer.searchView.search_result_views.views) {
      const rowsAndColumns = this.resultViewService.getDefaultCalculatedRowsColumns(searchResultView.name);
      if (rowsAndColumns) {
        searchContainer.rows[searchResultView.name] = rowsAndColumns.rows;
      }
    }
  }

}
